import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthHandlers } from "@/hooks/useAuthHandlers";

interface PasswordResetFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  title?: string;
  description?: string;
  submitButtonText?: string;
  showCancelButton?: boolean;
  userEmail?: string | null;
}

export const PasswordResetForm = ({
  onSuccess,
  onCancel,
  title = "Set New Password",
  description = "Enter your new password below.",
  submitButtonText = "Update Password",
  showCancelButton = false,
  userEmail,
}: PasswordResetFormProps) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const { isLoading, handleResetPassword, validatePassword } = useAuthHandlers();

  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;
  const passwordValidation = validatePassword(newPassword);
  const isValid = passwordsMatch && passwordValidation.valid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const success = await handleResetPassword(newPassword, confirmPassword);
    if (success) {
      setNewPassword("");
      setConfirmPassword("");
      onSuccess?.();
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
        {userEmail && (
          <p className="text-sm font-medium text-foreground">{userEmail}</p>
        )}
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="new-password">New Password</Label>
          <Input
            id="new-password"
            type="password"
            placeholder="••••••••"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
          {newPassword && !passwordValidation.valid && (
            <p className="text-sm text-destructive">{passwordValidation.error}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm New Password</Label>
          <Input
            id="confirm-password"
            type="password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
          {confirmPassword && !passwordsMatch && (
            <p className="text-sm text-destructive">Passwords do not match</p>
          )}
        </div>
        <div className={showCancelButton ? "flex gap-2" : ""}>
          <Button type="submit" className={showCancelButton ? "flex-1" : "w-full"} disabled={isLoading || !isValid}>
            {isLoading ? "Updating..." : submitButtonText}
          </Button>
          {showCancelButton && onCancel && (
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};
